"use client";

import React, { useState } from "react";
import { 
  Building, 
  Calendar, 
  Car, 
  DollarSign, 
  CheckCircle2, 
  Clock, 
  ShieldCheck, 
  X, 
  Send,
  FileText,
  KeyRound,
  Sparkles
} from "lucide-react";
import { RealEstateLead } from "@/lib/demos/realEstateDb";

interface RealEstateStageModalProps {
  lead: RealEstateLead;
  targetStage: "Site Visit Scheduled" | "Booking Token" | "Sale Deed (Won)";
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (options: {
    stage: RealEstateLead["stage"];
    site_visit_date?: string;
    site_visit_pickup?: number;
    options?: any;
  }) => Promise<void>;
}

export default function RealEstateStageModal({
  lead,
  targetStage,
  isOpen,
  onClose,
  onConfirm,
}: RealEstateStageModalProps) {
  const sym = lead.currency === "INR" ? "₹" : "$";
  const [submitting, setSubmitting] = useState(false);

  // Form states for Stage: Site Visit Scheduled
  const [visitDate, setVisitDate] = useState(
    lead.site_visit_date || "2026-09-08 11:30 AM"
  );
  const [hasPickup, setHasPickup] = useState(
    lead.site_visit_pickup !== undefined ? lead.site_visit_pickup === 1 : true
  );
  const [vehicle, setVehicle] = useState("Mercedes-Benz S-Class");

  // Form states for Stage: Booking Token
  const [tokenAmount, setTokenAmount] = useState(
    sym === "₹" ? "₹10,00,000" : "$25,000"
  );
  const [unitNumber, setUnitNumber] = useState(
    lead.property_type === "Penthouse" 
      ? "Tower A - Penthouse 2801" 
      : lead.property_type === "Villa"
      ? "Signature Villa #12"
      : "Executive Suite B-1402"
  );
  const [paymentMode, setPaymentMode] = useState("RTGS / Bank Wire Transfer");

  // Form states for Stage: Sale Deed (Won)
  const [deedRegNumber, setDeedRegNumber] = useState(
    `AP-DEED-${new Date().getFullYear()}-${Math.floor(1000 + Math.random() * 9000)}`
  );
  const [finalSaleValue, setFinalSaleValue] = useState(lead.budget_range);
  const [handoverTimeline, setHandoverTimeline] = useState("Q4 2026 (Immediate Handover)");
  const [notes, setNotes] = useState("");

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      if (targetStage === "Site Visit Scheduled") {
        await onConfirm({
          stage: "Site Visit Scheduled",
          site_visit_date: visitDate,
          site_visit_pickup: hasPickup ? 1 : 0,
          options: {
            siteVisitDate: visitDate,
            siteVisitPickup: hasPickup,
            chauffeurVehicle: vehicle,
            advisorNotes: notes.trim() || undefined,
          },
        });
      } else if (targetStage === "Booking Token") {
        await onConfirm({
          stage: "Booking Token",
          options: {
            tokenAmount,
            unitNumber,
            paymentMode,
            advisorNotes: notes.trim() || undefined,
          },
        });
      } else if (targetStage === "Sale Deed (Won)") {
        await onConfirm({
          stage: "Sale Deed (Won)",
          options: {
            deedRegNumber,
            finalSaleValue,
            handoverTimeline,
            advisorNotes: notes.trim() || undefined,
          },
        });
      }
      onClose();
    } catch (err) {
      console.error("Modal submission error:", err);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-950/80 backdrop-blur-md animate-in fade-in duration-200">
      <div 
        className="w-full max-w-lg rounded-2xl bg-slate-900 border border-slate-800 shadow-2xl overflow-hidden flex flex-col max-h-[90vh]"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="p-5 border-b border-slate-800 bg-gradient-to-r from-slate-950 to-slate-900 flex items-start justify-between">
          <div className="flex items-center gap-3">
            <div className="p-2.5 rounded-xl bg-amber-500/10 border border-amber-500/20 text-amber-400">
              {targetStage === "Site Visit Scheduled" ? (
                <Car className="w-5 h-5" />
              ) : targetStage === "Booking Token" ? (
                <ShieldCheck className="w-5 h-5" />
              ) : (
                <KeyRound className="w-5 h-5" />
              )}
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">
                  Required Stage Details
                </span>
                <span className="text-xs text-slate-400">Auto-sends Email</span>
              </div>
              <h3 className="text-lg font-bold text-white font-heading mt-0.5">
                {targetStage === "Site Visit Scheduled" && "Schedule VIP Site Inspection"}
                {targetStage === "Booking Token" && "Record Booking Token & Block Unit"}
                {targetStage === "Sale Deed (Won)" && "Execute Sale Deed & Confirm Deal"}
              </h3>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded-lg text-slate-400 hover:text-white hover:bg-slate-800 transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Lead Context Pill */}
        <div className="px-5 py-3 bg-slate-950/60 border-b border-slate-800/80 flex items-center justify-between text-xs">
          <div>
            <span className="text-slate-400">Client: </span>
            <strong className="text-white">{lead.name}</strong>
            <span className="text-slate-500 mx-1.5">•</span>
            <span className="text-slate-400">{lead.email}</span>
          </div>
          <span className="px-2 py-0.5 rounded bg-slate-800 text-amber-400 font-bold">
            {lead.property_name}
          </span>
        </div>

        {/* Form Body */}
        <form onSubmit={handleSubmit} className="p-5 overflow-y-auto space-y-4 flex-grow custom-scrollbar">
          {/* ========================================================= */}
          {/* STAGE: SITE VISIT SCHEDULED */}
          {/* ========================================================= */}
          {targetStage === "Site Visit Scheduled" && (
            <>
              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1.5 flex items-center gap-1.5">
                  <Calendar className="w-3.5 h-3.5 text-amber-400" />
                  Scheduled Inspection Date &amp; Time
                </label>
                <input
                  type="text"
                  value={visitDate}
                  onChange={(e) => setVisitDate(e.target.value)}
                  placeholder="e.g. 2026-09-08 11:30 AM"
                  required
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-sm text-white focus:outline-none focus:border-amber-500 transition-colors"
                />
              </div>

              <div className="p-3 rounded-xl bg-slate-950 border border-slate-800/90 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Car className="w-4 h-4 text-emerald-400" />
                    <div>
                      <h5 className="text-xs font-bold text-white">VIP Chauffeur Pickup</h5>
                      <p className="text-[11px] text-slate-400">Dispatch executive vehicle to client residence</p>
                    </div>
                  </div>
                  <input
                    type="checkbox"
                    checked={hasPickup}
                    onChange={(e) => setHasPickup(e.target.checked)}
                    className="w-4 h-4 rounded text-amber-500 focus:ring-amber-500 bg-slate-900 border-slate-700"
                  />
                </div>

                {hasPickup && (
                  <div>
                    <label className="block text-[11px] font-medium text-slate-400 mb-1">
                      Assigned Vehicle Model
                    </label>
                    <select
                      value={vehicle}
                      onChange={(e) => setVehicle(e.target.value)}
                      className="w-full px-3 py-2 rounded-lg bg-slate-900 border border-slate-800 text-xs text-white focus:outline-none focus:border-amber-500"
                    >
                      <option value="Mercedes-Benz S-Class">Mercedes-Benz S-Class</option>
                      <option value="BMW 7-Series Luxury Line">BMW 7-Series Luxury Line</option>
                      <option value="Range Rover Autobiography">Range Rover Autobiography</option>
                      <option value="Executive Mercedes V-Class (Family)">Executive Mercedes V-Class (Family)</option>
                    </select>
                  </div>
                )}
              </div>
            </>
          )}

          {/* ========================================================= */}
          {/* STAGE: BOOKING TOKEN (EOI) */}
          {/* ========================================================= */}
          {targetStage === "Booking Token" && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5 flex items-center gap-1.5">
                    <DollarSign className="w-3.5 h-3.5 text-amber-400" />
                    Token Deposit Amount
                  </label>
                  <input
                    type="text"
                    value={tokenAmount}
                    onChange={(e) => setTokenAmount(e.target.value)}
                    required
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-sm text-white focus:outline-none focus:border-amber-500"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5 flex items-center gap-1.5">
                    <Building className="w-3.5 h-3.5 text-amber-400" />
                    Allocated Signature Unit
                  </label>
                  <input
                    type="text"
                    value={unitNumber}
                    onChange={(e) => setUnitNumber(e.target.value)}
                    required
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-sm text-white focus:outline-none focus:border-amber-500"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1.5">
                  Payment Method / Ref
                </label>
                <select
                  value={paymentMode}
                  onChange={(e) => setPaymentMode(e.target.value)}
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-sm text-white focus:outline-none focus:border-amber-500"
                >
                  <option value="RTGS / Bank Wire Transfer">RTGS / Bank Wire Transfer</option>
                  <option value="Cheque / Banker's Demand Draft">Cheque / Banker's Demand Draft</option>
                  <option value="Escrow Holding Deposit">Escrow Holding Deposit</option>
                  <option value="Credit Card / Stripe Corporate">Credit Card / Stripe Corporate</option>
                </select>
              </div>
            </>
          )}

          {/* ========================================================= */}
          {/* STAGE: SALE DEED (WON) */}
          {/* ========================================================= */}
          {targetStage === "Sale Deed (Won)" && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5 flex items-center gap-1.5">
                    <FileText className="w-3.5 h-3.5 text-emerald-400" />
                    Deed Registry Ref #
                  </label>
                  <input
                    type="text"
                    value={deedRegNumber}
                    onChange={(e) => setDeedRegNumber(e.target.value)}
                    required
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-sm text-white focus:outline-none focus:border-emerald-500 font-mono"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-300 mb-1.5 flex items-center gap-1.5">
                    <DollarSign className="w-3.5 h-3.5 text-emerald-400" />
                    Final Agreement Value
                  </label>
                  <input
                    type="text"
                    value={finalSaleValue}
                    onChange={(e) => setFinalSaleValue(e.target.value)}
                    required
                    className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-sm text-white focus:outline-none focus:border-emerald-500 font-bold"
                  />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-slate-300 mb-1.5 flex items-center gap-1.5">
                  <KeyRound className="w-3.5 h-3.5 text-emerald-400" />
                  Possession &amp; Handover Schedule
                </label>
                <input
                  type="text"
                  value={handoverTimeline}
                  onChange={(e) => setHandoverTimeline(e.target.value)}
                  required
                  className="w-full px-3.5 py-2.5 rounded-xl bg-slate-950 border border-slate-800 text-sm text-white focus:outline-none focus:border-emerald-500"
                />
              </div>
            </>
          )}

          {/* Common Notes Area */}
          <div>
            <label className="block text-xs font-semibold text-slate-300 mb-1">
              Private Advisor Notes (Optional)
            </label>
            <textarea
              rows={2}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add specific instructions for client or concierge..."
              className="w-full px-3 py-2 rounded-xl bg-slate-950 border border-slate-800 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-amber-500"
            />
          </div>

          <div className="pt-3 border-t border-slate-800 flex items-center justify-end gap-2.5">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="px-4 py-2.5 rounded-xl bg-slate-800 hover:bg-slate-700 text-xs font-semibold text-slate-300 transition-colors"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={submitting}
              className="px-5 py-2.5 rounded-xl bg-gradient-to-r from-amber-500 to-amber-600 hover:from-amber-400 hover:to-amber-500 text-slate-950 font-bold text-xs flex items-center gap-1.5 shadow-lg shadow-amber-500/20 transition-all disabled:opacity-50"
            >
              {submitting ? (
                <>
                  <div className="w-3.5 h-3.5 border-2 border-slate-950 border-t-transparent rounded-full animate-spin" />
                  <span>Dispatching Email...</span>
                </>
              ) : (
                <>
                  <Send className="w-3.5 h-3.5" />
                  <span>Confirm &amp; Send Automated Email</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
